'use client';

import { useAction } from 'next-safe-action/hooks';
import { createContext, useContext, useMemo, useState } from 'react';
import { setCurrencyTaxStatusCookie } from '~/actions/currency/currency-actions';

export type CurrencyTaxStatus = 'tax-inclusive' | 'tax-exclusive';

type CurrencyContextModel = {
  currencyTaxStatus: CurrencyTaxStatus;
  changeCurrencyTaxStatus: () => void;
};

const CurrencyContext = createContext<CurrencyContextModel>({
  currencyTaxStatus: 'tax-exclusive',
  changeCurrencyTaxStatus: () => void null,
});

type CurrencyProviderProps = {
  initialCurrencyTaxStatus?: CurrencyTaxStatus;
  children: React.ReactNode;
};

export function CurrencyProvider({
  children,
  initialCurrencyTaxStatus,
}: CurrencyProviderProps) {
  const [currencyTaxStatus, setCurrencyTaxStatus] = useState<CurrencyTaxStatus>(
    initialCurrencyTaxStatus ?? 'tax-exclusive',
  );

  const taxStatusApi = useAction(setCurrencyTaxStatusCookie);

  const value = useMemo(() => {
    const handleCurrencyTaxStatusChange = () => {
      // call action to set cookie
      const newTaxstatus =
        currencyTaxStatus === 'tax-inclusive'
          ? 'tax-exclusive'
          : 'tax-inclusive';
      void taxStatusApi.executeAsync({ taxStatus: newTaxstatus });
      setCurrencyTaxStatus(newTaxstatus);
    };

    return {
      currencyTaxStatus,
      changeCurrencyTaxStatus: handleCurrencyTaxStatusChange,
    };
  }, [currencyTaxStatus, taxStatusApi]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrencyStatus = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useBasket must be used within a CurrencyProvider');
  }
  return context;
};

export { CurrencyContext };
